import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Polyfill URL.parse() static method for Safari < 17.4 / Chrome < 120 / Firefox < 115.
// pdfjs-dist >= 5.x relies on this API and throws "URL.parse is not a function" on
// older browsers. The polyfill matches the WHATWG spec: returns a URL object on
// success, null on failure (instead of throwing like `new URL(...)`).
if (typeof URL !== 'undefined' && typeof URL.parse !== 'function') {
  URL.parse = function parsePolyfill(url, base) {
    try {
      return base !== undefined && base !== null ? new URL(url, base) : new URL(url);
    } catch (e) {
      return null;
    }
  };
}

// Suppress harmless dev-only errors in the preview environment :
//   1. ResizeObserver loop (Radix UI / Select dropdowns)
//   2. "Blocked a frame with origin … cross-origin frame" — fired by the
//      React 19 dev profiler (`addValueToProperties` / `logComponentRender`)
//      when it walks props that contain any browsing-context element
//      (`<iframe>`, `<embed>`, `<object>`) loaded with a blob URL. The
//      profiler does `for (key in object)` then accesses `object[key]`,
//      which throws on cross-origin Window properties. This error does not
//      affect rendering nor exist in production builds.
const HARMLESS_PATTERNS = [
  'ResizeObserver loop',
  'Blocked a frame with origin',
  'cross-origin frame',
];

const isHarmlessMessage = (msg) => {
  if (typeof msg !== 'string') return false;
  return HARMLESS_PATTERNS.some((p) => msg.includes(p));
};

const suppressHarmlessError = (e) => {
  const msg = e?.message || e?.reason?.message || '';
  if (isHarmlessMessage(msg)) {
    e.stopImmediatePropagation?.();
    e.stopPropagation?.();
    e.preventDefault?.();
  }
};
window.addEventListener('error', suppressHarmlessError, true);
window.addEventListener('unhandledrejection', (e) => {
  const msg = e?.reason?.message || '';
  if (isHarmlessMessage(msg)) {
    e.preventDefault();
  }
});

// Also wrap console.error so the swallowed errors don't get logged at all
const _origConsoleError = console.error.bind(console);
console.error = (...args) => {
  try {
    const first = args[0];
    const text = typeof first === 'string'
      ? first
      : first?.message || (first ? String(first) : '');
    if (isHarmlessMessage(text)) return;
  } catch (_) { /* noop */ }
  _origConsoleError(...args);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register service worker for PWA standalone mode
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // updateViaCache: 'none' force le navigateur à toujours re-fetch sw.js
    // depuis le réseau (pas de cache HTTP) — sinon Safari peut garder
    // l'ancien SW plusieurs heures et bloquer la propagation des fixes.
    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        // Vérifie immédiatement s'il y a une mise à jour disponible.
        // `.update()` retourne une Promise — on doit la catcher sinon
        // une registration en état "invalid" déclenche un unhandled rejection
        // (ex. après hot-reload CRA en dev → React overlay rouge).
        Promise.resolve()
          .then(() => reg.update())
          .catch(() => {});
      })
      .catch(() => {});

    // Auto-reload silencieux quand un nouveau SW prend le contrôle de la
    // page. Sans ça, après un déploiement, le navigateur garde en mémoire
    // les anciens chunks JS déjà chargés (les imports lazy peuvent même
    // afficher l'ancienne version d'un composant alors que d'autres parties
    // de l'app sont déjà sur la nouvelle). On force donc un reload léger
    // dès que `controllerchange` est émis (= nouveau SW activé + claim).
    let hasReloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hasReloaded) return;
      hasReloaded = true;
      window.location.reload();
    });
  });
}
